/**
 * Fair Home V2 — Selected for you (H6b)
 * Figma RedisignHomePage `15:9720` desktop · `15:9569` compact
 * Editorial Selections cards — ≠ ProductCardV2
 */
import type { RecommendedBanner } from '@/types/BannersTypes'
import { mapFairHomeV2CapsulesAudience } from './fair-home-v2-capsules.data'

/** Match FairSelectionsCardV2 look widths */
export const FAIR_HOME_V2_SELECTED_CARD_WIDTH_DESKTOP = 560
export const FAIR_HOME_V2_SELECTED_CARD_WIDTH_COMPACT = 350

export const FAIR_HOME_V2_SELECTED_GAP_DESKTOP = 24
export const FAIR_HOME_V2_SELECTED_GAP_COMPACT = 12

export const FAIR_HOME_V2_SELECTED_LIMIT = 12

export type FairHomeV2SelectedPersonaId =
  | 'guest'
  | 'registered-partial'
  | 'newcomer'
  | 'profile-complete'

/** Prop default only — the page passes the live persona (H10). */
export const FAIR_HOME_V2_SELECTED_DEFAULT_PERSONA: FairHomeV2SelectedPersonaId =
  'registered-partial'

export interface FairHomeV2SelectedPersonaView {
  titleKey: string
  subtitleKey: string
  linkKey?: string
  showAiIcon: boolean
}

export interface FairHomeV2SelectedSlide {
  id: number
  title: string
  description: string
  imageUrl: string
  imageAlt?: string
}

const PERSONA_VIEWS: Record<
  FairHomeV2SelectedPersonaId,
  FairHomeV2SelectedPersonaView
> = {
  guest: {
    titleKey: 'fair_home_v2.selected.title_guest',
    subtitleKey: 'fair_home_v2.selected.subtitle_guest',
    linkKey: 'fair_home_v2.selected.link_sign_in',
    showAiIcon: true,
  },
  'registered-partial': {
    titleKey: 'fair_home_v2.selected.title_default',
    subtitleKey: 'fair_home_v2.selected.subtitle_partial',
    linkKey: 'fair_home_v2.selected.link_improve',
    showAiIcon: true,
  },
  newcomer: {
    titleKey: 'fair_home_v2.selected.title_default',
    subtitleKey: 'fair_home_v2.selected.subtitle_newcomer',
    linkKey: 'fair_home_v2.selected.link_style_quiz',
    showAiIcon: true,
  },
  'profile-complete': {
    titleKey: 'fair_home_v2.selected.title_default',
    subtitleKey: 'fair_home_v2.selected.subtitle_complete',
    showAiIcon: true,
  },
}

export function resolveFairHomeV2SelectedPersona(
  persona: FairHomeV2SelectedPersonaId = FAIR_HOME_V2_SELECTED_DEFAULT_PERSONA,
): FairHomeV2SelectedPersonaView {
  return PERSONA_VIEWS[persona]
}

interface BannerImageFields {
  imageUrl?: string
  image_url?: string
}

function getBannerImageUrl(banner: RecommendedBanner & BannerImageFields): string {
  return banner.imageUrl ?? banner.image_url ?? ''
}

function mapBannerToSlide(
  banner: RecommendedBanner & BannerImageFields,
): FairHomeV2SelectedSlide | null {
  const imageUrl = getBannerImageUrl(banner)
  if (!banner.id || !banner.name || !imageUrl) return null
  return {
    id: banner.id,
    title: banner.name,
    description: banner.description ?? '',
    imageUrl,
  }
}

export function resolveFairHomeV2SelectedSlides(
  banners: RecommendedBanner[] | undefined | null,
): FairHomeV2SelectedSlide[] {
  return (banners ?? [])
    .map(mapBannerToSlide)
    .filter((slide): slide is FairHomeV2SelectedSlide => slide != null)
    .slice(0, FAIR_HOME_V2_SELECTED_LIMIT)
}

/** The subtitle: the persona's, unless the API fell back to the catalogue's collections. */
export function resolveFairHomeV2SelectedSubtitleKey(
  source: 'audience' | 'catalogue' | undefined,
  personaSubtitleKey: string,
): string {
  return source === 'catalogue' ? 'fair_home_v2.selected.subtitle_catalogue' : personaSubtitleKey
}

export function mapFairHomeV2SelectedAudience(audience: string): string {
  return mapFairHomeV2CapsulesAudience(audience)
}

export function getFairHomeV2SelectedCardWidth(compact: boolean): number {
  return compact
    ? FAIR_HOME_V2_SELECTED_CARD_WIDTH_COMPACT
    : FAIR_HOME_V2_SELECTED_CARD_WIDTH_DESKTOP
}

export function getFairHomeV2SelectedGap(compact: boolean): number {
  return compact
    ? FAIR_HOME_V2_SELECTED_GAP_COMPACT
    : FAIR_HOME_V2_SELECTED_GAP_DESKTOP
}

export function getFairHomeV2SelectedCardsPerView(
  compact: boolean,
  viewportWidth: number,
): number {
  if (compact) return 1
  const cardWidth = FAIR_HOME_V2_SELECTED_CARD_WIDTH_DESKTOP
  const gap = FAIR_HOME_V2_SELECTED_GAP_DESKTOP
  const step = cardWidth + gap
  if (step <= 0) return 1
  return Math.max(1, Math.floor((viewportWidth + gap) / step))
}

export function getFairHomeV2SelectedPageCount(
  totalSlides: number,
  cardsPerView: number,
): number {
  if (totalSlides <= 0) return 1
  if (totalSlides <= cardsPerView) return 1
  return totalSlides - cardsPerView + 1
}

export function normalizeFairHomeV2SelectedPage(
  page: number,
  pageCount: number,
): number {
  const safeTotal = Math.max(1, pageCount)
  return Math.min(Math.max(0, Math.floor(page)), safeTotal - 1)
}

export function resolveFairHomeV2SelectedScrollPage(
  scrollLeft: number,
  step: number,
  pageCount: number,
): number {
  if (step <= 0) return 0
  return normalizeFairHomeV2SelectedPage(Math.round(scrollLeft / step), pageCount)
}
